// Ubicación y Numeración (Req. 6, 7): CRUD de segmentos con rack/posición,
// jerarquía de numeración configurable por tenant y análisis de impacto del
// recálculo global con preview + confirmación.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AlertMessage, AsyncBoundary, Card, Icon, PageHeader } from '../ui/components.js';
import type {
  GlobalRenumberPreview,
  NumberingHierarchyConfig,
  SortKey,
  SortLevel,
} from '../api/endpoints.js';
import type { Location } from '../types.js';

/** Normaliza la respuesta (puede venir como array o { items }). */
function toArray(data: { items: Location[] } | Location[]): Location[] {
  return Array.isArray(data) ? data : data.items;
}

/** Campos editables de un segmento (create/edit comparten forma). */
interface SegmentDraft {
  genre_label: string;
  subgenre_label: string;
  range_start: string;
  range_end: string;
  sort_order: string;
  rack: string;
  position: string;
  physical_place: string;
  notes: string;
}

const EMPTY_DRAFT: SegmentDraft = {
  genre_label: '',
  subgenre_label: '',
  range_start: '',
  range_end: '',
  sort_order: '',
  rack: '',
  position: '',
  physical_place: '',
  notes: '',
};

function draftFromLocation(l: Location): SegmentDraft {
  return {
    genre_label: l.genre_label,
    subgenre_label: l.subgenre_label ?? '',
    range_start: String(l.range_start),
    range_end: String(l.range_end),
    sort_order: l.sort_order != null ? String(l.sort_order) : '',
    rack: l.rack ?? '',
    position: l.position != null ? String(l.position) : '',
    physical_place: l.physical_place ?? '',
    notes: l.notes ?? '',
  };
}

function draftToInput(d: SegmentDraft): Partial<Location> {
  const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));
  return {
    genre_label: d.genre_label.trim(),
    subgenre_label: d.subgenre_label.trim() || null,
    range_start: Number(d.range_start),
    range_end: Number(d.range_end),
    sort_order: num(d.sort_order),
    rack: d.rack.trim() || null,
    position: num(d.position),
    physical_place: d.physical_place.trim() || null,
    notes: d.notes.trim() || null,
  };
}

export function LocationsView() {
  const { t } = useTranslation();
  const api = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('locations', 'read_write');
  const list = useAsync(() => api.locations.list(), []);
  // Conteo real de discos por segmento (reutiliza el análisis de impacto, que
  // ya carga los discos de cada segmento). Se refresca junto con la lista.
  const impact = useAsync(() => api.locations.renumberPreview(), []);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const countByLocation = new Map<string, number>();
  if (impact.data) {
    for (const seg of impact.data.segments) countByLocation.set(seg.location_id, seg.record_count);
  }

  function reloadAll() {
    list.reload();
    impact.reload();
  }

  async function remove(item: Location) {
    if (!confirm(t('locations.deleteConfirm', { name: item.genre_label }))) return;
    await api.locations.remove(item.id);
    reloadAll();
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('locations.pageTitle')}
        subtitle={t('locations.pageSubtitle')}
        back
        meta={
          canEdit ? (
            <button
              className="btn-secondary btn-sm"
              type="button"
              data-tour="locations-new"
              onClick={() => {
                setEditingId(null);
                setAdding((a) => !a);
              }}
            >
              <Icon name={adding ? 'close' : 'add'} className="text-[16px]" /> {adding ? t('locations.close') : t('locations.new')}
            </button>
          ) : undefined
        }
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      {/* Jerarquía de numeración + análisis de impacto (solo edición). */}
      {canEdit && <NumberingHierarchyCard onApplied={reloadAll} />}

      {adding && canEdit && (
        <SegmentForm
          title={t('locations.newLocation')}
          initial={EMPTY_DRAFT}
          onSubmit={async (draft) => {
            await api.locations.create(draftToInput(draft));
          }}
          onDone={() => {
            setAdding(false);
            reloadAll();
          }}
        />
      )}

      <AsyncBoundary state={list}>
        {(data) => {
          const items = toArray(data);
          if (items.length === 0)
            return (
              <div className="surface-card text-center text-on-surface-variant font-body-sm text-body-sm py-space-lg">
                {t('locations.empty')}
              </div>
            );
          return (
            <div className="flex flex-col gap-space-sm">
              <span className="font-code-catalog text-body-sm text-on-surface-variant">
                {t('locations.zonesActive', { count: items.length })}
              </span>
              {items.map((l) => {
                if (editingId === l.id) {
                  return (
                    <SegmentForm
                      key={l.id}
                      title={t('locations.editTitle', { name: l.genre_label })}
                      initial={draftFromLocation(l)}
                      onSubmit={async (draft) => {
                        await api.locations.update(l.id, draftToInput(draft));
                      }}
                      onDone={() => {
                        setEditingId(null);
                        reloadAll();
                      }}
                      onCancel={() => setEditingId(null)}
                    />
                  );
                }
                return (
                  <SegmentCard
                    key={l.id}
                    location={l}
                    count={countByLocation.get(l.id) ?? null}
                    canEdit={canEdit}
                    onEdit={() => {
                      setAdding(false);
                      setEditingId(l.id);
                    }}
                    onDelete={() => void remove(l)}
                  />
                );
              })}
            </div>
          );
        }}
      </AsyncBoundary>
      </div>
    </div>
  );
}

/** Tarjeta de un segmento con rack destacado, rango, capacidad y conteo real. */
function SegmentCard({
  location: l,
  count,
  canEdit,
  onEdit,
  onDelete,
}: {
  location: Location;
  count: number | null;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const capacity = l.range_end - l.range_start + 1;
  const over = count != null && count > capacity;
  return (
    <div className="surface-card flex flex-col gap-space-sm">
      <div className="flex items-start justify-between gap-space-sm">
        <div className="flex items-center gap-space-xs min-w-0">
          <span className="w-8 h-8 shrink-0 rounded bg-surface-container-high flex items-center justify-center text-primary">
            <Icon name="shelves" className="text-[18px]" />
          </span>
          <div className="min-w-0">
            <h3 className="font-headline-md text-headline-md text-on-surface tracking-tight truncate">
              {l.genre_label}
            </h3>
            {l.subgenre_label && (
              <span className="section-label text-on-surface-variant">{l.subgenre_label}</span>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-space-xs shrink-0">
            <button
              className="w-9 h-9 rounded bg-surface-container text-primary flex items-center justify-center"
              type="button"
              aria-label={t('locations.edit')}
              onClick={onEdit}
            >
              <Icon name="edit" className="text-[18px]" />
            </button>
            <button
              className="w-9 h-9 rounded bg-surface-container text-priority-urgent flex items-center justify-center"
              type="button"
              aria-label={t('locations.delete')}
              onClick={onDelete}
            >
              <Icon name="delete" className="text-[18px]" />
            </button>
          </div>
        )}
      </div>

      {/* Rack DESTACADO por sección (Req.: racks más visibles). */}
      {(l.rack || l.position != null) && (
        <div className="flex items-center gap-space-xs">
          {l.rack && (
            <span className="inline-flex items-center gap-1 px-space-sm py-1 rounded-lg bg-primary/15 text-primary font-code-catalog text-headline-sm font-bold tracking-wide">
              <Icon name="inventory_2" className="text-[18px]" />
              {l.rack}
            </span>
          )}
          {l.position != null && (
            <span className="inline-flex items-center px-space-sm py-1 rounded-lg bg-surface-container-high text-on-surface font-code-catalog text-body-md font-bold">
              {t('locations.position', { n: l.position })}
            </span>
          )}
        </div>
      )}

      <div className="p-space-sm rounded-lg bg-surface-base flex items-center justify-between gap-space-sm">
        <div className="flex flex-col">
          <span className="section-label text-on-surface-variant">{t('locations.assignedRange')}</span>
          <span className="font-code-catalog text-headline-sm text-primary font-bold tracking-wider">
            #{String(l.range_start).padStart(4, '0')} — #{String(l.range_end).padStart(4, '0')}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="section-label text-on-surface-variant">{t('locations.recordsCapacity')}</span>
          <span
            className={`font-code-catalog text-body-md font-bold ${over ? 'text-priority-urgent' : 'text-on-surface'}`}
          >
            {count != null ? count : '—'} / {capacity}
          </span>
        </div>
      </div>

      {over && (
        <div className="flex items-center gap-space-xs text-priority-urgent font-body-sm text-body-sm">
          <Icon name="warning" className="text-[16px]" />
          {t('locations.overCapacity')}
        </div>
      )}

      {l.physical_place && (
        <div className="flex items-center gap-space-xs text-on-surface-variant font-body-sm text-body-sm">
          <Icon name="location_on" className="text-[16px]" />
          {l.physical_place}
        </div>
      )}
      {l.notes && (
        <div className="flex items-center gap-space-xs text-on-surface-variant font-body-sm text-body-sm">
          <Icon name="notes" className="text-[16px]" />
          {l.notes}
        </div>
      )}
    </div>
  );
}

/** Formulario reutilizable para crear o editar un segmento. */
function SegmentForm({
  title,
  initial,
  onSubmit,
  onDone,
  onCancel,
}: {
  title: string;
  initial: SegmentDraft;
  onSubmit: (draft: SegmentDraft) => Promise<void>;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SegmentDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof SegmentDraft>(key: K, value: string) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(draft);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('locations.errSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={title} icon="edit_location">
      <form onSubmit={submit} className="flex flex-col gap-space-sm">
        <div className="grid grid-cols-2 gap-space-sm">
          <Field label={t('locations.fieldGenre')}>
            <input className="field-input" value={draft.genre_label} onChange={(e) => set('genre_label', e.target.value)} required />
          </Field>
          <Field label={t('locations.fieldSubgenre')}>
            <input className="field-input" value={draft.subgenre_label} onChange={(e) => set('subgenre_label', e.target.value)} />
          </Field>
          <Field label={t('locations.fieldRangeStart')}>
            <input className="field-input" type="number" value={draft.range_start} onChange={(e) => set('range_start', e.target.value)} required />
          </Field>
          <Field label={t('locations.fieldRangeEnd')}>
            <input className="field-input" type="number" value={draft.range_end} onChange={(e) => set('range_end', e.target.value)} required />
          </Field>
          <Field label={t('locations.fieldRack')}>
            <input className="field-input" value={draft.rack} onChange={(e) => set('rack', e.target.value)} placeholder={t('locations.rackPlaceholder')} />
          </Field>
          <Field label={t('locations.fieldPosition')}>
            <input className="field-input" type="number" min="1" value={draft.position} onChange={(e) => set('position', e.target.value)} />
          </Field>
          <Field label={t('locations.fieldSortOrder')}>
            <input className="field-input" type="number" value={draft.sort_order} onChange={(e) => set('sort_order', e.target.value)} placeholder={t('locations.sortOrderPlaceholder')} />
          </Field>
          <Field label={t('locations.fieldPhysicalPlace')}>
            <input className="field-input" value={draft.physical_place} onChange={(e) => set('physical_place', e.target.value)} />
          </Field>
        </div>
        <Field label={t('locations.fieldNotes')}>
          <input className="field-input" value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        <AlertMessage message={error} />
        <div className="flex gap-space-sm">
          <button className="btn-primary flex-1" type="submit" disabled={busy}>
            {busy ? t('locations.saving') : t('locations.save')}
          </button>
          {onCancel && (
            <button className="btn-secondary" type="button" onClick={onCancel} disabled={busy}>
              {t('locations.cancel')}
            </button>
          )}
        </div>
      </form>
    </Card>
  );
}

/**
 * Tarjeta de configuración de la jerarquía de numeración: reordenar niveles,
 * cambiar dirección, guardar y ejecutar el análisis de impacto (preview) con
 * confirmación antes de aplicar el recálculo global.
 */
function NumberingHierarchyCard({ onApplied }: { onApplied: () => void }) {
  const api = useApi();
  const config = useAsync<NumberingHierarchyConfig>(() => api.config.getNumberingHierarchy(), []);
  return (
    <AsyncBoundary state={config}>
      {(data) => <HierarchyEditor config={data} onApplied={onApplied} onReload={() => config.reload()} />}
    </AsyncBoundary>
  );
}

function HierarchyEditor({
  config,
  onApplied,
  onReload,
}: {
  config: NumberingHierarchyConfig;
  onApplied: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  // Etiqueta legible de una variable de ordenación.
  const keyLabel = (key: SortKey): string => t(`locations.key.${key}`);
  const [levels, setLevels] = useState<SortLevel[]>(config.hierarchy);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<GlobalRenumberPreview | null>(null);
  const [open, setOpen] = useState(false);

  function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= levels.length) return;
    const copy = [...levels];
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item!);
    setLevels(copy);
    setPreview(null);
  }

  function toggleDir(index: number) {
    setLevels((ls) =>
      ls.map((l, i) => (i === index ? { ...l, direction: l.direction === 'asc' ? 'desc' : 'asc' } : l)),
    );
    setPreview(null);
  }

  /** Quita un nivel de la regla; la variable vuelve a estar disponible. */
  function removeLevel(index: number) {
    setLevels((ls) => ls.filter((_, i) => i !== index));
    setPreview(null);
  }

  /** Agrega una variable disponible al final de la regla (dirección asc). */
  function addLevel(key: SortKey) {
    if (levels.some((l) => l.key === key)) return;
    setLevels((ls) => [...ls, { key, direction: 'asc' }]);
    setPreview(null);
  }

  // Variables del catálogo cerrado (config.keys) que aún NO están en la regla.
  const availableKeys = config.keys.filter((k) => !levels.some((l) => l.key === k.key));

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await api.config.setNumberingHierarchy(levels);
      setMsg(t('locations.savedHierarchy'));
      onReload();
    } finally {
      setBusy(false);
    }
  }

  async function analyze() {
    setBusy(true);
    setMsg(null);
    try {
      // Guarda primero para que el preview use la jerarquía elegida.
      await api.config.setNumberingHierarchy(levels);
      const result = await api.locations.renumberPreview();
      setPreview(result);
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!preview) return;
    if (!confirm(t('locations.applyConfirm', { count: preview.total_changes }))) return;
    setBusy(true);
    setMsg(null);
    try {
      const result = await api.locations.renumberApply();
      setMsg(t('locations.recalcApplied', { count: result.total_changes }));
      setPreview(null);
      onApplied();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('locations.hierarchyTitle')} icon="sort">
      <div className="flex flex-col gap-space-sm">
        <button
          className="btn-secondary btn-sm self-start"
          type="button"
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[16px]" />
          {open ? t('locations.hide') : t('locations.configureOrder')}
        </button>

        {open && (
          <>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {t('locations.hierarchyHint')}
            </p>
            <div className="flex flex-col gap-space-xs">
              {levels.map((level, i) => (
                <div key={level.key} className="flex items-center gap-space-xs p-space-xs rounded-lg bg-surface-base">
                  <span className="font-code-catalog text-body-sm text-on-surface-variant w-5 text-center">{i + 1}</span>
                  <span className="flex-1 font-body-md text-body-md text-on-surface">{keyLabel(level.key)}</span>
                  <button
                    className="px-space-xs py-1 rounded bg-surface-container text-on-surface font-body-sm text-body-sm"
                    type="button"
                    onClick={() => toggleDir(i)}
                    title={t('locations.changeDirection')}
                  >
                    {level.direction === 'asc' ? 'A→Z' : 'Z→A'}
                  </button>
                  <button
                    className="w-8 h-8 rounded bg-surface-container text-on-surface flex items-center justify-center disabled:opacity-40"
                    type="button"
                    aria-label={t('locations.moveUp')}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <Icon name="arrow_upward" className="text-[16px]" />
                  </button>
                  <button
                    className="w-8 h-8 rounded bg-surface-container text-on-surface flex items-center justify-center disabled:opacity-40"
                    type="button"
                    aria-label={t('locations.moveDown')}
                    disabled={i === levels.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <Icon name="arrow_downward" className="text-[16px]" />
                  </button>
                  <button
                    className="w-8 h-8 rounded bg-surface-container text-priority-urgent flex items-center justify-center disabled:opacity-40"
                    type="button"
                    aria-label={t('locations.removeVariable')}
                    title={t('locations.removeFromRule')}
                    disabled={levels.length <= 1}
                    onClick={() => removeLevel(i)}
                  >
                    <Icon name="close" className="text-[16px]" />
                  </button>
                </div>
              ))}
            </div>

            {/* Añadir variables disponibles (las que no están en la regla). */}
            {availableKeys.length > 0 && (
              <div className="flex flex-wrap items-center gap-space-xs">
                <span className="section-label text-on-surface-variant">{t('locations.addVariable')}</span>
                {availableKeys.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    className="px-space-sm py-1 rounded-full bg-surface-container text-on-surface font-body-sm text-body-sm flex items-center gap-1 hover:bg-surface-rack"
                    onClick={() => addLevel(k.key)}
                  >
                    <Icon name="add" className="text-[16px]" />
                    {keyLabel(k.key)}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-space-sm">
              <button className="btn-secondary" type="button" onClick={() => void save()} disabled={busy}>
                {t('locations.saveOrder')}
              </button>
              <button className="btn-primary" type="button" onClick={() => void analyze()} disabled={busy}>
                {busy ? t('locations.analyzing') : t('locations.analyzeImpact')}
              </button>
            </div>

            {preview && (
              <div className="p-space-sm rounded-lg bg-surface-container flex flex-col gap-space-xs">
                <div className="flex items-center justify-between">
                  <span className="section-label text-on-surface-variant">{t('locations.impact')}</span>
                  <span className="font-code-catalog text-headline-sm text-primary font-bold">
                    {preview.total_changes} / {preview.total_records}
                  </span>
                </div>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  {t('locations.impactDesc', { changes: preview.total_changes, total: preview.total_records })}
                  {preview.overflow_segments > 0 && (
                    <span className="text-priority-urgent">
                      {t('locations.overflowSegments', { count: preview.overflow_segments })}
                    </span>
                  )}
                </p>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={() => void apply()}
                  disabled={busy || preview.total_changes === 0}
                >
                  {preview.total_changes === 0 ? t('locations.noChanges') : t('locations.applyRecalc')}
                </button>
              </div>
            )}

            {msg && <p className="font-body-sm text-body-sm text-vacuum-glow">{msg}</p>}
          </>
        )}
      </div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-xs">
      <label className="field-label">{label}</label>
      <div className="field-slot">{children}</div>
    </div>
  );
}
